import React, { FormEvent } from "react";
import { Building2, Package, Plus } from "lucide-react";
import { matchCase, IdGenerator } from "src/utils";
import {
  CreatePurchaseOrderFormData,
  CreatePurchaseOrderFormDataKey,
  CreatePurchaseOrderFormAction,
  CreatePOProductInputField
} from "src/types";
import { ValidationErrors } from "src/errors";
import { useScrollOnMessage } from "src/hooks";
import { CreatePOProductInputTable } from "src/components/tables/inputTables";
import { TextInputBox } from "src/components/ui/inputs";
import { FormLvlErrorMessage, ValidationErrorMessage } from "src/components/ui/feedback";
import { SubmissionTooltip } from "src/components/tooltips";

interface CreatePurchaseOrderFormProps {
  formData: CreatePurchaseOrderFormData;
  vendorOptions: string[];
  validationErrors?: ValidationErrors<CreatePurchaseOrderFormDataKey>;
  formLvlErrorMessage?: string;
  isFormComplete: boolean;
  isInternalHealfOrderDisabled?: boolean;
  handleInputChange: (
    field: CreatePurchaseOrderFormDataKey,
    value: CreatePurchaseOrderFormData[keyof CreatePurchaseOrderFormData]
  ) => void;
  handleSubmit: (e?: FormEvent) => Promise<void>;
  setAction: (action: CreatePurchaseOrderFormAction) => void;
}

const CreatePurchaseOrderForm: React.FC<CreatePurchaseOrderFormProps> = ({
  formData,
  vendorOptions,
  validationErrors,
  formLvlErrorMessage,
  isFormComplete,
  isInternalHealfOrderDisabled = true,
  handleInputChange,
  handleSubmit,
  setAction
}) => {
  const formLvlErrorMessageRef = useScrollOnMessage(formLvlErrorMessage);

  const handleSetProducts: React.Dispatch<React.SetStateAction<CreatePOProductInputField[]>> = (updatedProducts) => {
    handleInputChange("products", updatedProducts as CreatePOProductInputField[]);
  };

  const handleAddNewProduct = () => {
    const newProduct: CreatePOProductInputField = {
      fieldId: IdGenerator.generateUniqueElementId(),
      productId: "",
      ukQuantity: "",
      euQuantity: "",
      extraInfo: ""
    };
    handleSetProducts([...formData.products, newProduct]);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto">
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
          {/* Header Section */}
          <div className="bg-black px-8 py-6">
            <h1 className="text-3xl font-bold text-white tracking-tight">Create Purchase Order</h1>
            <p className="text-gray-300 mt-2 text-lg">Create a new purchase order manually</p>
          </div>

          <form onSubmit={handleSubmit} autoComplete="off" className="p-8">
            <div className="space-y-6">
              {/* Vendor Selection Section */}
              <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                  <Building2 className="w-4 h-4 mr-2" />
                  Vendor
                </h3>
                <TextInputBox
                  id="vendor"
                  placeholder="Enter vendor name to search..."
                  onChange={(value: string) => handleInputChange("vendorName", value)}
                  value={formData.vendorName}
                  onBlur={() => handleInputChange("vendorName", matchCase(formData.vendorName, vendorOptions))}
                  dropDownOptions={vendorOptions}
                />
                <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("vendorName")} />
              </div>

              {/* Products Section */}
              <div className="border border-neutral-200 rounded-2xl p-6 bg-white">
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-black tracking-wide uppercase flex items-center">
                    <Package className="w-4 h-4 mr-2" />
                    Products
                  </h3>
                  <p className="text-xs text-neutral-600 mt-1">
                    Add products by entering their Product ID (6-15 digits) with UK quantity (sent to GXO for internal
                    orders) and EU quantity. Prices will be fetched automatically from the cost sheet.
                  </p>
                </div>

                {formData.products.length > 0 ? (
                  <>
                    <CreatePOProductInputTable products={formData.products} setProducts={handleSetProducts} />
                    <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("products")} />
                  </>
                ) : (
                  <div className="bg-neutral-50 border border-dashed border-neutral-300 rounded-lg p-8 text-center">
                    <Package className="w-12 h-12 mx-auto mb-3 text-neutral-400" />
                    <p className="text-sm text-neutral-600 mb-4">No products added yet</p>
                    <p className="text-xs text-neutral-500">Click the button below to add products to this PO</p>
                  </div>
                )}

                <div className="mt-4">
                  <button
                    className="flex items-center space-x-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium shadow-sm"
                    onClick={handleAddNewProduct}
                    type="button"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Product</span>
                  </button>
                </div>
              </div>

              {/* Form Level Error */}
              <FormLvlErrorMessage message={formLvlErrorMessage} centred={false} ref={formLvlErrorMessageRef} />

              {/* Internal Healf Order Checkbox */}
              <div className="flex items-center gap-3 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg">
                <input
                  type="checkbox"
                  id="internalHealfOrder"
                  checked={formData.isInternalHealfOrder}
                  onChange={(e) => handleInputChange("isInternalHealfOrder", e.target.checked)}
                  disabled={isInternalHealfOrderDisabled}
                  className={`w-4 h-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2 ${isInternalHealfOrderDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                />
                <label
                  htmlFor="internalHealfOrder"
                  className={`text-sm font-medium text-gray-900 ${isInternalHealfOrderDisabled ? "cursor-default" : "cursor-pointer"}`}
                >
                  Internal Healf Order
                </label>
                <span className="text-xs text-gray-600 ml-2">
                  (Auto-confirms and sends to GXO without vendor confirmation)
                </span>
              </div>

              {/* Submit Button */}
              <div className="flex items-center gap-4">
                <button
                  className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                  disabled={!isFormComplete}
                  onClick={() => setAction("CREATE")}
                  type="submit"
                >
                  Create Purchase Order
                </button>

                {!isFormComplete && <SubmissionTooltip formState="INCOMPLETE" />}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export { CreatePurchaseOrderForm };
